import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReferrerBranchesPanel from "@/components/app/ReferrerBranchesPanel";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash, Copy, PencilSimple, User, MagnifyingGlass, LinkSimple, ChatCircle, Browser, Palette, Image as ImageIcon, X as XIcon, Check, Key, Buildings, Bank, CaretDown, CaretRight,
} from "@phosphor-icons/react";

/**
 * Compact relative-time formatter for the "Logged in 2d ago" pill. Returns
 * "just now" inside a minute, then minutes/hours/days for the first month,
 * and falls back to "DD MMM" for older sign-ins.
 */
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (Number.isNaN(diff)) return "";
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function ReferrersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [referrers, setReferrers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [collapsedCompanies, setCollapsedCompanies] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [saving, setSaving] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(false);
  // Credential-management dialog (set/reset referrer password). Opens from
  // the Key icon on each row. Solicitor can type a chosen password or
  // generate a random one; saving fires POST /referrers/{id}/invite.
  const [credentialReferrer, setCredentialReferrer] = useState(null);
  const [credentialPwd, setCredentialPwd] = useState("");
  const [credentialSaving, setCredentialSaving] = useState(false);
  const [credentialShowPwd, setCredentialShowPwd] = useState(false);
  // Deep-link target: `?focus=<referrer_id>` (set from the Analytics
  // leaderboard). Scrolls the row into view and pulses a highlight ring.
  const [focusId, setFocusId] = useState(searchParams.get("focus") || null);

  const fetchReferrers = useCallback(async () => {
    try {
      const { data } = await API.get("/referrers");
      setReferrers(data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchReferrers();
    // Auto-refresh every 30s so the unread-message badge stays fresh while open.
    const id = setInterval(fetchReferrers, 30000);
    return () => clearInterval(id);
  }, [fetchReferrers]);

  // Once the referrers list is in, scroll the focused row into view and
  // schedule a brief highlight pulse, then strip the param from the URL
  // so the highlight doesn't persist on refresh.
  useEffect(() => {
    if (!focusId || referrers.length === 0) return;
    const el = document.querySelector(`[data-testid="referrer-row-${focusId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const timeout = setTimeout(() => {
      setFocusId(null);
      // Remove ?focus=... so subsequent reloads don't re-highlight.
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }, 2500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, referrers.length]);

  const openCreate = () => {
    setEditingReferrer(null);
    setForm({ name: "", email: "", phone: "", company: "" });
    setDialogOpen(true);
  };

  const [activeEditTab, setActiveEditTab] = useState("details");

  const openEdit = (r, tab = "details") => {
    setEditingReferrer(r);
    setForm({ name: r.name, email: r.email || "", phone: r.phone || "", company: r.company || "" });
    setActiveEditTab(tab);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingReferrer) {
        await API.put(`/referrers/${editingReferrer.id}`, form);
        toast.success("Referrer updated");
      } else {
        const { data } = await API.post("/referrers", form);
        const wm = data?.welcome_email;
        if (wm?.sent) {
          toast.success(`Referrer created — welcome email sent (${wm.mode})`);
        } else if (form.email) {
          toast.success("Referrer created — welcome email could not be sent");
        } else {
          toast.success("Referrer created");
        }
      }
      setDialogOpen(false);
      fetchReferrers();
    } catch { toast.error("Failed to save referrer"); }
    finally { setSaving(false); }
  };

  const handleResendWelcome = async (referrer) => {
    if (!referrer?.email) { toast.error("Add an email first"); return; }
    setResendingWelcome(true);
    try {
      const { data } = await API.post(`/referrers/${referrer.id}/resend-welcome`);
      if (data?.sent) {
        toast.success(`Welcome email re-sent (${data.mode})`);
        fetchReferrers();
      } else {
        toast.error(data?.error || "Could not send the welcome email");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setResendingWelcome(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete referrer "${name}"?`)) return;
    try {
      await API.delete(`/referrers/${id}`);
      toast.success("Referrer deleted");
      fetchReferrers();
    } catch { toast.error("Failed to delete"); }
  };

  const copyLink = (token) => {
    try {
      navigator.clipboard.writeText(`${window.location.origin}/referrer-dashboard/${token}`)
        .then(() => toast.success("Referrer dashboard link copied"))
        .catch(() => toast.error("Couldn't copy — please copy manually"));
    } catch {
      toast.error("Clipboard not available");
    }
  };

  // Random temp-password generator — 12-char alphanumeric, ambiguous chars
  // (0/O, 1/l/I) excluded so it's safe to relay verbally.
  const generateRandomPassword = () => {
    const charset = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789abcdefghjkmnpqrstuvwxyz";
    return Array.from({ length: 12 }, () =>
      charset[Math.floor(Math.random() * charset.length)]
    ).join("");
  };

  // Open the "Set / reset password" dialog for this referrer. Pre-fills with
  // a freshly-generated password so the solicitor can either keep it or
  // overwrite with their own choice.
  const handleSendInvite = (referrer) => {
    if (!referrer.email) {
      toast.error("Referrer needs an email on file first");
      return;
    }
    setCredentialReferrer(referrer);
    setCredentialPwd(generateRandomPassword());
    setCredentialShowPwd(true);
  };

  // Persist the chosen password against the referrer's user account and
  // copy it to clipboard so the solicitor can relay it. Triggers the
  // existing welcome-email mock in the backend.
  const handleSaveCredentials = async () => {
    if (!credentialReferrer) return;
    if (credentialPwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setCredentialSaving(true);
    try {
      await API.post(`/referrers/${credentialReferrer.id}/invite`, {
        temp_password: credentialPwd,
      });
      try {
        await navigator.clipboard.writeText(credentialPwd);
        toast.success(
          `Password saved · copied to clipboard. Share with ${credentialReferrer.name}.`,
          { duration: 8000 },
        );
      } catch {
        toast.success(
          `Password saved. Share this with ${credentialReferrer.name}: ${credentialPwd}`,
          { duration: 12000 },
        );
      }
      setCredentialReferrer(null);
      setCredentialPwd("");
      // Refresh so the "Magic-link only" pill flips to "Logged in" once they sign in.
      fetchReferrers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save credentials");
    } finally {
      setCredentialSaving(false);
    }
  };

  const isBrandingComplete = (r) => !!(r.use_brand && r.brand_logo_url && r.brand_primary);
  const pendingCount = referrers.filter((r) => !isBrandingComplete(r)).length;
  const filtered = referrers
    .filter((r) =>
      !search || r.name.toLowerCase().includes(search.toLowerCase()) ||
      (r.company || "").toLowerCase().includes(search.toLowerCase()) ||
      (r.email || "").toLowerCase().includes(search.toLowerCase())
    )
    .filter((r) => !onlyPending || !isBrandingComplete(r));

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const totalUnread = referrers.reduce((s, r) => s + (r.unread_count || 0), 0);
  const referrersWithUnread = referrers.filter((r) => (r.unread_count || 0) > 0).length;

  const goToUnread = (r) => {
    if (!r.latest_unread_case_id) return;
    // Hop into the case detail with the Notes tab pre-focused on the referrer panel.
    navigate(`/cases/${r.latest_unread_case_id}?tab=notes&focus=referrer`);
  };

  // Build company groupings for the "By company" view. Solo / un-companied
  // referrers are bucketed under a synthetic "No company" group at the end.
  const companyGroups = (() => {
    const map = new Map();
    for (const r of filtered) {
      const key = (r.company || "").trim() || "__nocompany__";
      if (!map.has(key)) map.set(key, { label: key === "__nocompany__" ? "(No company)" : r.company, members: [] });
      map.get(key).members.push(r);
    }
    // Stable alphabetical order, with "No company" last
    const groups = Array.from(map.entries()).map(([key, g]) => ({ key, ...g }));
    groups.sort((a, b) => {
      if (a.key === "__nocompany__") return 1;
      if (b.key === "__nocompany__") return -1;
      return a.label.localeCompare(b.label);
    });
    return groups;
  })();

  // Inline row renderer — reused in both the flat list and the grouped view.
  const renderRow = (r) => {
    const unread = r.unread_count || 0;
    const payOverride = !!r.payment_details_override;
    const brandOverride = !!r.brand_override;
    return (
      <div key={r.id} className={`bg-white border rounded-lg p-4 shadow-sm flex items-center justify-between transition-shadow ${focusId === r.id ? "border-emerald-500 ring-4 ring-emerald-200/60 animate-pulse" : unread > 0 ? "border-[#c87d3a]/40 shadow-[0_0_0_3px_rgba(200,125,58,0.08)]" : "border-slate-200"}`} data-testid={`referrer-row-${r.id}`}>
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center shrink-0 relative">
            <User weight="bold" className="w-5 h-5 text-violet-600" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid={`unread-dot-${r.id}`}>
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-slate-900">{r.name}</p>
              {r.company && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{r.company}</span>}
              {/* Override status pills — only show when the referrer belongs
                  to a company (otherwise there's nothing to "override"). */}
              {r.company && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${payOverride ? "bg-rose-50 text-rose-700" : "bg-indigo-50 text-indigo-700"}`}
                  data-testid={`payment-override-pill-${r.id}`}
                  title={payOverride ? "Personal payment-details override — not affected by company-wide changes" : "Follows the company default payment details"}
                >
                  <Bank weight="bold" className="w-3 h-3" />
                  {payOverride ? "Own payment" : "Co. payment"}
                </span>
              )}
              {r.company && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${brandOverride ? "bg-rose-50 text-rose-700" : "bg-indigo-50 text-indigo-700"}`}
                  data-testid={`brand-override-pill-${r.id}`}
                  title={brandOverride ? "Personal branding override — not affected by company-wide changes" : "Follows the company default branding"}
                >
                  <Palette weight="bold" className="w-3 h-3" />
                  {brandOverride ? "Own brand" : "Co. brand"}
                </span>
              )}
              {(() => {
                const hasLogo = !!r.brand_logo_url;
                const hasColors = !!r.brand_primary;
                const branded = r.use_brand && hasLogo && hasColors;
                if (branded) {
                  return (
                    <button
                      type="button"
                      onClick={() => openEdit(r, "branding")}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full hover:opacity-80 transition-opacity cursor-pointer"
                      style={{ background: "rgba(16,185,129,0.12)", color: "#047857" }}
                      title="Branding complete — click to edit"
                      data-testid={`branding-status-${r.id}`}
                    >
                      <Check weight="bold" className="w-3 h-3" /> Branded
                    </button>
                  );
                }
                const parts = [];
                if (!hasLogo) parts.push("logo");
                if (!hasColors) parts.push("colours");
                if (hasLogo && hasColors && !r.use_brand) parts.push("toggle");
                return (
                  <button
                    type="button"
                    onClick={() => openEdit(r, "branding")}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer"
                    title={`Branding pending — missing ${parts.join(" + ")}. Click to set it up.`}
                    data-testid={`branding-status-${r.id}`}
                  >
                    Branding pending
                  </button>
                );
              })()}
              {unread > 0 && (
                <button onClick={() => goToUnread(r)}
                  data-testid={`new-messages-pill-${r.id}`}
                  title={r.latest_unread_excerpt}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full text-white hover:opacity-90 transition-opacity"
                  style={{ background: '#c87d3a' }}>
                  <ChatCircle weight="fill" className="w-3 h-3" />
                  {unread} new message{unread === 1 ? "" : "s"} · open case
                </button>
              )}
              {/* Login status — green when the referrer has actually
                  signed in via the new password / Google flow,
                  amber when they only have the legacy magic link. */}
              {r.last_login_at ? (
                <span
                  data-testid={`login-status-${r.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                  title={`Last sign-in: ${new Date(r.last_login_at).toLocaleString("en-GB")} · ${r.last_login_method === "google" ? "Google" : "Password"}`}
                >
                  <Check weight="bold" className="w-3 h-3" />
                  Logged in {timeAgo(r.last_login_at)}
                </span>
              ) : r.email ? (
                <span
                  data-testid={`login-status-${r.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700"
                  title="Hasn't signed in yet — still uses the magic link"
                >
                  Magic-link only
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
              {r.email && <span>{r.email}</span>}
              {r.phone && <span>{r.phone}</span>}
              {unread > 0 && r.latest_unread_excerpt && (
                <span className="italic text-slate-400 truncate max-w-[260px]" title={r.latest_unread_excerpt}>
                  "{r.latest_unread_excerpt}"
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => copyLink(r.access_token)} data-testid={`copy-link-${r.id}`}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-violet-100 text-slate-400 hover:text-violet-600 transition-colors"
            title="Copy dashboard link">
            <LinkSimple weight="bold" className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => handleSendInvite(r)} data-testid={`invite-referrer-${r.id}`}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-100 text-slate-400 hover:text-amber-600 transition-colors disabled:opacity-40"
            disabled={!r.email}
            title={r.email ? (r.last_login_at ? "Reset sign-in password" : "Set sign-in password") : "Add an email first"}>
            <Key weight="bold" className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => openEdit(r)} data-testid={`edit-referrer-${r.id}`}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <PencilSimple weight="bold" className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => handleDelete(r.id, r.name)} data-testid={`delete-referrer-${r.id}`}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors">
            <Trash weight="bold" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="referrers-page">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Referrers</h1>
            <p className="text-sm text-slate-500 mt-0.5">{referrers.length} referrer{referrers.length !== 1 ? "s" : ""}</p>
          </div>
          <Button onClick={openCreate} className="h-9 text-sm bg-slate-900 hover:bg-slate-800 text-white gap-1.5" data-testid="add-referrer-btn">
            <Plus weight="bold" className="w-4 h-4" /> Add Referrer
          </Button>
        </div>

        {totalUnread > 0 && (
          <div className="mb-4 rounded-lg border flex items-center gap-3 px-4 py-3" style={{ background: 'rgba(200,125,58,0.08)', borderColor: 'rgba(200,125,58,0.35)' }} data-testid="unread-summary-banner">
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: '#c87d3a' }}>
              <ChatCircle weight="fill" className="w-4 h-4 text-white" />
            </div>
            <div className="text-sm">
              <span className="font-semibold" style={{ color: '#100f5d' }}>{totalUnread}</span> unread message{totalUnread === 1 ? "" : "s"} from <span className="font-semibold" style={{ color: '#100f5d' }}>{referrersWithUnread}</span> referrer{referrersWithUnread === 1 ? "" : "s"}
              <span className="text-slate-500"> — click any badge below to jump to the case.</span>
            </div>
          </div>
        )}

        <div className="relative mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="referrer-search" placeholder="Search referrers..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-10 h-10 bg-white" />
          </div>
          <button
            type="button"
            onClick={() => setOnlyPending((p) => !p)}
            disabled={pendingCount === 0 && !onlyPending}
            data-testid="filter-branding-pending-btn"
            className={`h-10 shrink-0 inline-flex items-center gap-2 px-3 rounded-lg border text-xs font-semibold transition-all ${
              onlyPending
                ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
                : "bg-white border-slate-200 text-slate-600 hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            title={onlyPending ? "Showing only branding-pending — click to show all" : `Show only referrers whose branding isn't finished (${pendingCount} pending)`}
          >
            {onlyPending ? (
              <>
                <XIcon weight="bold" className="w-3.5 h-3.5" /> Branding pending · {pendingCount}
              </>
            ) : (
              <>
                <Palette weight="bold" className="w-3.5 h-3.5" /> Branding pending
                {pendingCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px]">{pendingCount}</span>}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setGroupByCompany((g) => !g)}
            data-testid="group-by-company-btn"
            className={`h-10 shrink-0 inline-flex items-center gap-2 px-3 rounded-lg border text-xs font-semibold transition-all ${
              groupByCompany
                ? "bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700"
                : "bg-white border-slate-200 text-slate-600 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700"
            }`}
            title={groupByCompany ? "Showing one card per company — click for a flat list" : "Group referrers by company and surface payment / branding overrides"}
          >
            <Buildings weight="bold" className="w-3.5 h-3.5" />
            {groupByCompany ? "By company · on" : "By company"}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-slate-300 border-t-blue-600 rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg" data-testid="empty-referrers">
            <User weight="thin" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-medium">
              {onlyPending
                ? "Every referrer's branding is complete — nice work"
                : referrers.length === 0
                  ? "No referrers yet"
                  : "No matching referrers"}
            </p>
            {onlyPending && (
              <button onClick={() => setOnlyPending(false)} className="mt-3 text-xs font-medium text-amber-700 hover:underline" data-testid="clear-pending-filter-btn">
                Show all referrers
              </button>
            )}
          </div>
        ) : groupByCompany ? (
          <div className="space-y-4" data-testid="referrers-by-company">
            {companyGroups.map((g) => {
              const m = g.members;
              const isCollapsed = !!collapsedCompanies[g.key];
              const payOverrides = m.filter((r) => r.payment_details_override).length;
              const brandOverrides = m.filter((r) => r.brand_override).length;
              const isSynthetic = g.key === "__nocompany__";
              return (
                <div key={g.key} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={`company-group-${g.key}`}>
                  <button
                    type="button"
                    onClick={() => setCollapsedCompanies((p) => ({ ...p, [g.key]: !p[g.key] }))}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`company-toggle-${g.key}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {isCollapsed
                        ? <CaretRight weight="bold" className="w-3.5 h-3.5 text-slate-400" />
                        : <CaretDown weight="bold" className="w-3.5 h-3.5 text-slate-400" />}
                      <Buildings weight="bold" className={`w-4 h-4 ${isSynthetic ? "text-slate-300" : "text-indigo-600"}`} />
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold tracking-tight ${isSynthetic ? "text-slate-400 italic" : "text-slate-900"}`}>{g.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {m.length} member{m.length === 1 ? "" : "s"}
                          {!isSynthetic && (
                            <>
                              {" · "}
                              <span className="inline-flex items-center gap-1">
                                <Bank weight="bold" className="w-2.5 h-2.5" />
                                <span className={payOverrides ? "text-rose-700 font-medium" : "text-emerald-700 font-medium"}>
                                  {m.length - payOverrides}/{m.length} on company payment
                                </span>
                              </span>
                              {" · "}
                              <span className="inline-flex items-center gap-1">
                                <Palette weight="bold" className="w-2.5 h-2.5" />
                                <span className={brandOverrides ? "text-rose-700 font-medium" : "text-emerald-700 font-medium"}>
                                  {m.length - brandOverrides}/{m.length} on company brand
                                </span>
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                  {!isCollapsed && (
                    <div className="px-4 pb-4 pt-1 space-y-2 border-t border-slate-100">
                      {m.map(renderRow)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(renderRow)}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>{editingReferrer ? "Edit Referrer" : "Add Referrer"}</DialogTitle>
            </DialogHeader>
            {editingReferrer ? (
              <Tabs value={activeEditTab} onValueChange={setActiveEditTab} className="w-full">
                <TabsList className="mb-3">
                  <TabsTrigger value="details" data-testid="referrer-tab-details">Details</TabsTrigger>
                  <TabsTrigger value="fees" data-testid="referrer-tab-fees">Fees</TabsTrigger>
                  <TabsTrigger value="branding" data-testid="referrer-tab-branding">Branding</TabsTrigger>
                  <TabsTrigger value="branches" data-testid="referrer-tab-branches">Branches</TabsTrigger>
                  <TabsTrigger value="embed" data-testid="referrer-tab-embed">Embed Code</TabsTrigger>
                </TabsList>
                <TabsContent value="details" className="mt-0">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Name *</Label>
                      <Input data-testid="referrer-name-field" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Company</Label>
                      <Input data-testid="referrer-company-field" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company / Agency" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Email</Label>
                        <Input data-testid="referrer-email-field" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@example.com" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Phone</Label>
                        <Input data-testid="referrer-phone-field" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+44..." />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={handleSave} disabled={saving || !form.name.trim()} data-testid="save-referrer-btn"
                        className="flex-1 bg-slate-900 hover:bg-slate-800 text-white">
                        {saving ? "Saving..." : "Update Referrer"}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleResendWelcome(editingReferrer)}
                        disabled={!editingReferrer?.email || resendingWelcome}
                        title={editingReferrer?.email ? "Re-send the setup-branding welcome email" : "Add an email first"}
                        variant="outline"
                        className="h-10 text-xs gap-1.5 shrink-0"
                        data-testid="resend-welcome-btn"
                      >
                        <ChatCircle weight="bold" className="w-3.5 h-3.5" /> {resendingWelcome ? "Sending…" : "Re-send welcome"}
                      </Button>
                    </div>
                    {editingReferrer?.welcome_email_sent_at && (
                      <p className="text-[10px] text-slate-400 text-right">
                        Welcome email last sent {new Date(editingReferrer.welcome_email_sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="fees" className="mt-0">
                  <ReferrerFeesPanel referrer={editingReferrer} onSaved={fetchReferrers} />
                </TabsContent>
                <TabsContent value="branding" className="mt-0">
                  <ReferrerBrandingPanel referrer={editingReferrer} onSaved={fetchReferrers} setEditingReferrer={setEditingReferrer} />
                </TabsContent>
                <TabsContent value="branches" className="mt-0">
                  <ReferrerBranchesPanel referrer={editingReferrer} onSaved={fetchReferrers} />
                </TabsContent>
                <TabsContent value="embed" className="mt-0">
                  <ReferrerEmbedPanel referrer={editingReferrer} />
                </TabsContent>
              </Tabs>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Name *</Label>
                  <Input data-testid="referrer-name-field" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Company</Label>
                  <Input data-testid="referrer-company-field" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company / Agency" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input data-testid="referrer-email-field" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="email@example.com" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone</Label>
                    <Input data-testid="referrer-phone-field" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+44..." />
                  </div>
                </div>
                <Button onClick={handleSave} disabled={saving || !form.name.trim()} data-testid="save-referrer-btn"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                  {saving ? "Saving..." : "Add Referrer"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Set / reset referrer password */}
        <Dialog
          open={!!credentialReferrer}
          onOpenChange={(open) => {
            if (!open) {
              setCredentialReferrer(null);
              setCredentialPwd("");
            }
          }}
        >
          <DialogContent className="sm:max-w-md" data-testid="credential-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
                Set sign-in password
              </DialogTitle>
            </DialogHeader>
            {credentialReferrer && (
              <div className="space-y-4">
                <div className="text-xs text-slate-500 leading-relaxed">
                  Set a password for{" "}
                  <span className="font-semibold text-slate-700">{credentialReferrer.name}</span>{" "}
                  ({credentialReferrer.email}). They will be able to sign in
                  immediately at <span className="font-mono text-slate-700">/referrer-login</span>{" "}
                  using this password.
                  {credentialReferrer.last_login_at ? (
                    <span className="block mt-1.5 text-emerald-700">
                      ⚠ This referrer has already signed in once — saving will{" "}
                      <span className="font-semibold">reset</span> their password.
                    </span>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cred-pwd" className="text-xs font-medium text-slate-700">
                    Password
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="cred-pwd"
                      data-testid="credential-pwd-input"
                      type={credentialShowPwd ? "text" : "password"}
                      value={credentialPwd}
                      onChange={(e) => setCredentialPwd(e.target.value)}
                      className="font-mono text-sm"
                      autoComplete="off"
                      minLength={8}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCredentialShowPwd((v) => !v)}
                      data-testid="credential-toggle-visible"
                      className="shrink-0"
                    >
                      {credentialShowPwd ? "Hide" : "Show"}
                    </Button>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>Min 8 characters · {credentialPwd.length} typed</span>
                    <button
                      type="button"
                      onClick={() => setCredentialPwd(generateRandomPassword())}
                      data-testid="credential-regen-btn"
                      className="font-medium text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline"
                    >
                      Generate a new one
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCredentialReferrer(null);
                      setCredentialPwd("");
                    }}
                    data-testid="credential-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSaveCredentials}
                    disabled={credentialSaving || credentialPwd.length < 8}
                    data-testid="credential-save-btn"
                    style={{ background: "#100f5d", color: "#fff" }}
                  >
                    {credentialSaving ? "Saving…" : "Save & copy"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}


// ==================== Per-referrer Fees panel ====================
// Shown inside the referrer edit dialog under the "Fees" tab.
// Admin-only: only admin solicitors hit this UI; the API also enforces admin.
const FEE_GROUPS = [
  {
    label: "Legal fees",
    hint: "Override the firm-wide quote — leave blank to inherit the default shown beside each input.",
    fields: [
      ["legal_fee_buying", "Buying"],
      ["legal_fee_selling", "Selling"],
      ["legal_fee_both", "Buying + Selling (combined)"],
      ["legal_fee_both_purchase", "Buy & Sell — Purchase leg"],
      ["legal_fee_both_sale", "Buy & Sell — Sale leg"],
      ["legal_fee_remortgage", "Remortgage"],
      ["legal_fee_transfer", "Transfer of Equity"],
      ["legal_fee_grant_of_lease", "Grant of Lease"],
    ],
  },
  {
    label: "Supplements",
    hint: "Optional surcharges added when the relevant box is ticked on the quote tool.",
    subgroups: [
      {
        tag: "Leasehold (per type)",
        tagColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
        fields: [
          ["leasehold_fee_buying", "Buying"],
          ["leasehold_fee_selling", "Selling"],
          ["leasehold_fee_both", "Buy & Sell"],
          ["leasehold_fee_remortgage", "Remortgage"],
          ["leasehold_fee_transfer", "Transfer of Equity"],
          ["leasehold_fee_grant_of_lease", "Grant of Lease"],
        ],
      },
      {
        tag: "BSA (per type)",
        tagColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
        fields: [
          ["bsa_fee_buying", "Buying"],
          ["bsa_fee_selling", "Selling"],
          ["bsa_fee_both", "Buy & Sell"],
          ["bsa_fee_remortgage", "Remortgage"],
          ["bsa_fee_transfer", "Transfer of Equity"],
          ["bsa_fee_grant_of_lease", "Grant of Lease"],
        ],
      },
      {
        tag: "Purchase",
        tagColor: "bg-blue-50 text-blue-700 border-blue-200",
        fields: [
          ["lender_fee", "Mortgage Lender Fee"],
          ["new_build_supplement", "New Build"],
          ["gifted_deposit_fee_per_giftor", "Gifted Deposit (per giftor)"],
          ["htb_isa_fee", "Help to Buy ISA"],
          ["lifetime_isa_fee", "Lifetime ISA"],
        ],
      },
      {
        tag: "Sale",
        tagColor: "bg-amber-50 text-amber-700 border-amber-200",
        fields: [
          ["mortgage_redemption_fee", "Mortgage Redemption"],
          ["htb_loan_redemption_fee", "HTB Loan Redemption"],
        ],
      },
      {
        tag: "Transfer of Equity",
        tagColor: "bg-violet-50 text-violet-700 border-violet-200",
        fields: [
          ["new_mortgage_fee", "New Mortgage"],
        ],
      },
      {
        tag: "Universal",
        tagColor: "bg-slate-100 text-slate-600 border-slate-200",
        fields: [
          ["shared_ownership_supplement", "Shared Ownership"],
          ["ltd_company_supplement", "Ltd Company"],
          ["auction_supplement", "Auction"],
          ["right_to_buy_supplement", "Right to Buy"],
          ["bridging_finance_supplement", "Bridging Finance"],
          ["commercial_supplement", "Commercial / Mixed-Use"],
          ["existing_mortgage_fee", "Existing Mortgage (Remo + TOE)"],
          ["leasehold_fee", "Leasehold (legacy fallback)"],
          ["bsa_fee", "BSA (legacy fallback)"],
        ],
      },
    ],
  },
  {
    label: "Commission rates (what we pay this referrer)",
    hint: "Auto-filled into the Bespoke Quote Tool when this referrer generates a quote — folded into the displayed Solicitor's Fee.",
    subgroups: [
      {
        tag: "Single-leg",
        tagColor: "bg-blue-50 text-blue-700 border-blue-200",
        fields: [
          ["commission_buying", "Buying"],
          ["commission_selling", "Selling"],
          ["commission_remortgage", "Remortgage"],
          ["commission_transfer", "Transfer of Equity"],
          ["commission_grant_of_lease", "Grant of Lease"],
        ],
      },
      {
        tag: "Buying & Selling",
        tagColor: "bg-amber-50 text-amber-700 border-amber-200",
        fields: [
          ["commission_purchase", "Purchase default"],
          ["commission_sale", "Sale default"],
          ["commission_both", "Combined (legacy — falls back if per-leg unset)"],
        ],
      },
    ],
  },
];

function ReferrerFeesPanel({ referrer, onSaved }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Company cascade override flag — same UX pattern as payment + branding.
  // false (default) → save cascades to all colleagues at the same company who
  // also have fees_override=false. true → personal override, this referrer is
  // skipped by future cascades.
  const [feesOverride, setFeesOverride] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await API.get(`/referrers/${referrer.id}/fees`);
        if (cancelled) return;
        setData(d);
        setFeesOverride(!!d.fees_override);
        // Convert overrides (numbers / null) to strings for the inputs.
        // ``custom_fees`` is a uuid -> amount map and is kept as-is (just
        // stringified) so the row inputs can read/write it directly.
        const f = {};
        for (const [k, v] of Object.entries(d.fee_overrides || {})) {
          if (k === "custom_fees") {
            const cfMap = {};
            for (const [cfId, cfAmt] of Object.entries(v || {})) {
              cfMap[cfId] = cfAmt === null || cfAmt === undefined ? "" : String(cfAmt);
            }
            f.custom_fees = cfMap;
          } else {
            f[k] = v === null || v === undefined ? "" : String(v);
          }
        }
        if (!f.custom_fees) f.custom_fees = {};
        setForm(f);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load fees");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [referrer.id]);

  if (!user?.is_admin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
        Per-referrer fees are admin-only. Ask an admin solicitor to set bespoke pricing.
      </div>
    );
  }

  if (loading) return <div className="py-12 text-center text-sm text-slate-400">Loading fees…</div>;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setCustomFee = (cfId, v) => setForm((p) => ({
    ...p,
    custom_fees: { ...(p.custom_fees || {}), [cfId]: v },
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (k === "custom_fees") {
          const cfPayload = {};
          for (const [cfId, cfAmt] of Object.entries(v || {})) {
            if (cfAmt !== "" && cfAmt !== null && cfAmt !== undefined) {
              cfPayload[cfId] = Number(cfAmt);
            }
          }
          payload.custom_fees = Object.keys(cfPayload).length ? cfPayload : null;
        } else {
          payload[k] = v === "" ? null : Number(v);
        }
      }
      await API.put(`/referrers/${referrer.id}/fees`, { ...payload, override: feesOverride });
      toast.success(
        feesOverride
          ? "Saved as personal fees for this referrer"
          : data?.company
            ? `Saved & applied to everyone at ${data.company}`
            : "Bespoke fees saved"
      );
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const copyWebTool = () => {
    const url = `${window.location.origin}/referrers-web-tool?ref_token=${referrer.access_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Bespoke Quote Tool link copied");
  };

  const firmDefaults = data?.firm_defaults || {};

  return (
    <div className="space-y-4" data-testid="referrer-fees-panel">
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: "rgba(16,15,93,0.04)", border: "1px solid rgba(16,15,93,0.12)" }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#100f5d" }}>
          <Browser weight="fill" className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold" style={{ color: "#100f5d" }}>{(referrer.company || referrer.name)} · Bespoke Quote Tool link</p>
          <p className="text-[11px] text-slate-500 truncate">Shareable URL — these fees auto-apply when the referrer generates a quote.</p>
        </div>
        <Button onClick={copyWebTool} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="copy-web-tool-btn">
          <Copy weight="bold" className="w-3.5 h-3.5" /> Copy link
        </Button>
      </div>

      {/* Company cascade toggle — only renders when this referrer belongs
          to a company. Indigo = save will auto-apply to every colleague
          who isn't overriding. Rose = personal override (won't cascade). */}
      {data?.company && (
        <button
          type="button"
          onClick={() => setFeesOverride((v) => !v)}
          data-testid="fees-override-toggle"
          className={`w-full text-[11px] font-medium px-3 py-2 rounded-md border inline-flex items-center justify-between gap-2 transition-colors ${
            feesOverride
              ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
              : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
          }`}
          title={feesOverride
            ? "Save updates only this referrer. Click to share the fee structure with the rest of the company."
            : `Save will auto-apply these fees to every referrer at "${data.company}" (unless they have their own override). Click to make this referrer's fees personal.`}
        >
          <span className="inline-flex items-center gap-1.5">
            <Browser weight="bold" className="w-3 h-3" />
            {feesOverride
              ? <>Using <strong className="font-semibold">personal fees</strong> — click to share with <strong className="font-semibold">{data.company}</strong></>
              : <>Auto-applies to everyone at <strong className="font-semibold">{data.company}</strong> — click to override</>}
          </span>
          <span className="text-[10px] uppercase tracking-wide font-bold opacity-70">
            {feesOverride ? "Personal" : "Company"}
          </span>
        </button>
      )}

      {FEE_GROUPS.map((group) => (
        <div key={group.label}>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">{group.label}</h4>
          <p className="text-[11px] text-slate-500 mb-2">{group.hint}</p>
          {group.subgroups ? (
            <div className="space-y-3">
              {group.subgroups.map((sg) => (
                <div key={sg.tag} className="space-y-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold tracking-wide uppercase ${sg.tagColor}`} data-testid={`referrer-fee-subgroup-${sg.tag.toLowerCase().replace(/\s+/g, '-')}`}>
                    {sg.tag}
                  </span>
                  <div className="grid grid-cols-2 gap-3 pl-1">
                    {sg.fields.map(([key, label]) => {
                      const def = firmDefaults[key];
                      const placeholder = def != null ? `Inherits £${def}` : "Optional";
                      return (
                        <div key={key} className="space-y-1">
                          <Label className="text-[11px] font-medium text-slate-600">{label}</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                            <Input type="number" step="0.01" min="0" value={form[key] || ""}
                              onChange={(e) => set(key, e.target.value)} placeholder={placeholder}
                              data-testid={`fee-input-${key}`} className="pl-7 h-9 text-sm" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {group.fields.map(([key, label]) => {
                const def = firmDefaults[key];
                const placeholder = def != null ? `Inherits £${def}` : "Optional";
                return (
                  <div key={key} className="space-y-1">
                    <Label className="text-[11px] font-medium text-slate-600">{label}</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form[key] || ""}
                        onChange={(e) => set(key, e.target.value)}
                        placeholder={placeholder}
                        data-testid={`fee-input-${key}`}
                        className="pl-7 h-9 text-sm"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}

      {Array.isArray(data?.custom_fees_catalog) && data.custom_fees_catalog.length > 0 && (
        <div data-testid="referrer-custom-fees-overrides">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Custom fees / disbursements</h4>
          <p className="text-[11px] text-slate-500 mb-2">Admin-defined items that auto-apply to every matching quote. Leave blank to inherit the firm £ amount.</p>
          <div className="space-y-2">
            {data.custom_fees_catalog.map((cf) => {
              const placeholder = cf.amount != null ? `Inherits £${cf.amount}` : "Optional";
              const types = (cf.transaction_types || []).join(" · ");
              const catColor = cf.category === "disbursement"
                ? "bg-lime-50 text-lime-700 border-lime-200"
                : cf.category === "legal"
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-cyan-50 text-cyan-700 border-cyan-200";
              return (
                <div key={cf.id} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-3 py-2 bg-slate-50">
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-xs font-semibold text-slate-800">{cf.name}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      <span className={`inline-flex items-center px-1.5 py-0 rounded-full border text-[9px] font-semibold tracking-wide uppercase ${catColor}`}>
                        {cf.category}
                      </span>
                      <span className="text-[10px] text-slate-500">{types}</span>
                    </div>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                    <Input
                      type="number" step="0.01" min="0"
                      value={(form.custom_fees || {})[cf.id] || ""}
                      onChange={(e) => setCustomFee(cf.id, e.target.value)}
                      placeholder={placeholder}
                      data-testid={`fee-input-custom-${cf.id}`}
                      className="pl-7 h-9 text-sm w-32 bg-white"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Button onClick={handleSave} disabled={saving} data-testid="save-referrer-fees-btn"
        className="w-full text-white hover:opacity-90" style={{ background: "#100f5d" }}>
        {saving ? "Saving…" : "Save bespoke fees"}
      </Button>
    </div>
  );
}


// ==================== Per-referrer Embed Code panel ====================
// Shown inside the referrer edit dialog under the "Embed Code" tab.
// Generates copy-paste snippets so the referrer can drop the bespoke quote
// tool onto their own website. The widget loads /referrers-web-tool with the
// referrer's access_token, so their custom fees + commission are auto-applied.
function ReferrerEmbedPanel({ referrer }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const token = referrer?.access_token || "";
  const safeCompany = (referrer?.company || referrer?.name || "Referrer").replace(/"/g, "'");

  // When the referrer has saved branding overrides, include them as data-attrs
  // on the <script> tag so the modal chrome + button colour match their brand.
  const brand = referrer?.use_brand ? {
    primary: referrer.brand_primary || "",
    accent: referrer.brand_accent || "",
    title: referrer.brand_modal_title || `${safeCompany} · Instant Quote`,
  } : null;
  const brandAttrs = brand
    ? `\n        data-brand-color="${brand.primary}"${brand.accent ? `\n        data-brand-accent="${brand.accent}"` : ''}\n        data-modal-title="${brand.title}"`
    : "";
  const triggerStyleColor = brand?.primary || '#100f5d';

  const floatingSnippet = `<!-- UKC Legal — ${safeCompany} · Bespoke Quote Tool -->
<script src="${origin}/embed.js"
        data-base="${origin}"
        data-mode="button"
        data-ref-token="${token}"
        data-cta-label="Get an Instant Quote"${brandAttrs}
        defer></script>`;

  const triggerSnippet = `<!-- UKC Legal — open the quote tool when this button is clicked -->
<button type="button" data-ukc-quote
        style="background:${triggerStyleColor};color:#fff;border:0;padding:12px 22px;border-radius:9999px;font-weight:600;cursor:pointer;">
  Get an Instant Quote
</button>
<script src="${origin}/embed.js"
        data-base="${origin}"
        data-mode="trigger"
        data-ref-token="${token}"${brandAttrs}
        defer></script>`;

  const autoSnippet = `<!-- UKC Legal — auto-open the modal on page load -->
<script src="${origin}/embed.js"
        data-base="${origin}"
        data-mode="auto"
        data-ref-token="${token}"${brandAttrs}
        defer></script>`;

  const directLink = `${origin}/referrers-web-tool?ref_token=${token}`;
  const previewQs = brand
    ? `?ref_token=${token}${brand.primary ? `&brand_color=${encodeURIComponent(brand.primary)}` : ''}${brand.accent ? `&brand_accent=${encodeURIComponent(brand.accent)}` : ''}${brand.title ? `&modal_title=${encodeURIComponent(brand.title)}` : ''}`
    : `?ref_token=${token}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (!token) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
        This referrer has no access token yet. Save the referrer first to generate one.
      </div>
    );
  }

  const Snippet = ({ title, hint, code, testId }) => (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between gap-3" style={{ background: "rgba(16,15,93,0.04)" }}>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-900 truncate">{title}</p>
          <p className="text-[10px] text-slate-500 leading-snug">{hint}</p>
        </div>
        <Button onClick={() => copy(code, title)} variant="outline" size="sm" className="h-7 text-[11px] gap-1.5 shrink-0" data-testid={`copy-${testId}-btn`}>
          <Copy weight="bold" className="w-3 h-3" /> Copy
        </Button>
      </div>
      <pre className="text-[10.5px] text-slate-700 font-mono p-3 bg-slate-50/80 overflow-x-auto whitespace-pre leading-relaxed" data-testid={`snippet-${testId}`}>{code}</pre>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="referrer-embed-panel">
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: "rgba(200,125,58,0.08)", border: "1px solid rgba(200,125,58,0.2)" }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "#c87d3a" }}>
          <Browser weight="fill" className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold" style={{ color: "#c87d3a" }}>Embed the Bespoke Quote Tool on {referrer.company || referrer.name}'s website</p>
          <p className="text-[11px] text-slate-600 leading-snug">Their custom fees and commission rates are auto-applied. Quotes generated through these snippets are automatically attributed to this referrer.</p>
        </div>
      </div>

      <Snippet
        title="Floating button (recommended)"
        hint="Adds a navy 'Get an Instant Quote' pill in the bottom-right of every page."
        code={floatingSnippet}
        testId="floating"
      />

      <Snippet
        title="Custom trigger button"
        hint="Use any element marked with [data-ukc-quote] to open the modal. Style the button to match the referrer's brand."
        code={triggerSnippet}
        testId="trigger"
      />

      <Snippet
        title="Auto-open on page load"
        hint="Opens the quote modal automatically — best for landing pages and email funnels."
        code={autoSnippet}
        testId="auto"
      />

      <div className="border-t border-slate-100 pt-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Or share a direct link</p>
        <div className="flex items-center gap-2">
          <Input readOnly value={directLink} className="text-xs h-8 font-mono bg-slate-50" data-testid="direct-link-input" onFocus={(e) => e.target.select()} />
          <Button onClick={() => copy(directLink, "Direct link")} variant="outline" size="sm" className="h-8 text-xs gap-1.5 shrink-0" data-testid="copy-direct-link-btn">
            <Copy weight="bold" className="w-3 h-3" /> Copy
          </Button>
          <a
            href={`${origin}/embed-test.html${previewQs}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-embed-preview-link"
            className="h-8 inline-flex items-center gap-1.5 px-3 rounded-md text-xs font-medium text-white hover:opacity-90 shrink-0"
            style={{ background: "#100f5d" }}
            title="Open a sample landing page with this referrer's bespoke tool embedded"
          >
            <Browser weight="bold" className="w-3 h-3" /> Open preview
          </a>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">Preview opens a sample landing page with the floating widget already wired to {referrer.company || referrer.name}'s token — exactly what a visitor would see.</p>
      </div>

      <div className="text-[10px] text-slate-400 leading-snug pt-1">
        <p><strong>Analytics hook:</strong> the host page receives <code className="bg-slate-100 rounded px-1">document.addEventListener('ukc:quote-generated', e =&gt; ...)</code> with <code className="bg-slate-100 rounded px-1">e.detail.quote_id</code>, <code className="bg-slate-100 rounded px-1">e.detail.total</code>, and <code className="bg-slate-100 rounded px-1">e.detail.client_name</code> on each successful quote.</p>
      </div>
    </div>
  );
}


// ==================== Per-referrer Branding panel ====================
// Shown inside the referrer edit dialog under the "Branding" tab. Lets admins
// upload the referrer's logo and pick custom colours so the Bespoke Quote Tool
// (and the embed modal chrome) match the referrer's brand instead of UKC's.
function ReferrerBrandingPanel({ referrer, onSaved, setEditingReferrer }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    use_brand: false,
    brand_primary: "",
    brand_accent: "",
    brand_text_on_primary: "",
    brand_logo_url: "",
    brand_modal_title: "",
    brand_footer_text: "",
    brand_terms_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useState({ current: null })[0];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await API.get(`/referrers/${referrer.id}/branding`);
        if (cancelled) return;
        setData(d);
        setForm({
          use_brand: !!d.use_brand,
          brand_primary: d.brand_primary || "",
          brand_accent: d.brand_accent || "",
          brand_text_on_primary: d.brand_text_on_primary || "",
          brand_logo_url: d.brand_logo_url || "",
          brand_modal_title: d.brand_modal_title || "",
          brand_footer_text: d.brand_footer_text || "",
          brand_terms_url: d.brand_terms_url || "",
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load branding");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [referrer.id]);

  if (loading) return <div className="py-12 text-center text-sm text-slate-400">Loading branding…</div>;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        use_brand: !!form.use_brand,
        brand_primary: form.brand_primary.trim(),
        brand_accent: form.brand_accent.trim(),
        brand_text_on_primary: form.brand_text_on_primary.trim(),
        brand_logo_url: form.brand_logo_url.trim(),
        brand_modal_title: form.brand_modal_title.trim(),
        brand_footer_text: form.brand_footer_text.trim(),
        brand_terms_url: form.brand_terms_url.trim(),
      };
      const { data: d } = await API.put(`/referrers/${referrer.id}/branding`, payload);
      setData(d);
      toast.success("Branding saved");
      // Keep parent referrer object in sync so other tabs (Embed Code) re-render
      // with the new brand colours injected into the snippets.
      if (setEditingReferrer) {
        setEditingReferrer((prev) => prev ? { ...prev, ...d } : prev);
      }
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be 2MB or smaller"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Logo must be an image"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await API.post(`/referrers/${referrer.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("brand_logo_url", r.brand_logo_url);
      toast.success("Logo uploaded — remember to save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleLogoClear = async () => {
    try {
      await API.delete(`/referrers/${referrer.id}/logo`);
      set("brand_logo_url", "");
      toast.success("Logo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear logo");
    }
  };

  const previewPrimary = form.brand_primary || "#100f5d";
  const previewAccent = form.brand_accent || "#c87d3a";
  const previewText = form.brand_text_on_primary || "#ffffff";
  const logoSrc = form.brand_logo_url
    ? (form.brand_logo_url.startsWith("/") ? `${origin}${form.brand_logo_url}` : form.brand_logo_url)
    : "/logo.png";

  return (
    <div className="space-y-4" data-testid="referrer-branding-panel">
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: "rgba(16,15,93,0.04)", border: "1px solid rgba(16,15,93,0.12)" }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: previewPrimary }}>
          <Palette weight="fill" className="w-4 h-4" style={{ color: previewText }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold" style={{ color: "#100f5d" }}>Custom branding for {referrer.company || referrer.name}</p>
          <p className="text-[11px] text-slate-500 leading-snug">Replaces UKC navy + copper on this referrer's quote tool. Leave colours blank to inherit defaults.</p>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={!!form.use_brand}
          onChange={(e) => set("use_brand", e.target.checked)}
          className="rounded border-slate-300"
          data-testid="branding-use-brand-cb"
        />
        <span><span className="font-semibold">Apply custom branding</span> — when off, falls back to UKC navy + copper.</span>
      </label>

      {/* Logo */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Logo</h4>
        <p className="text-[11px] text-slate-500 -mt-1">Shown at the top-left of the bespoke quote page (max 2MB, PNG/JPG/SVG/WebP). White-on-transparent works best on dark backgrounds.</p>
        <div className="flex items-center gap-3">
          <div className="h-14 w-32 rounded-md flex items-center justify-center overflow-hidden border border-slate-200" style={{ background: previewPrimary }}>
            <img src={logoSrc} alt="logo preview" className="max-h-12 max-w-full object-contain" data-testid="branding-logo-preview" onError={(e) => { e.currentTarget.src = "/logo.png"; }} />
          </div>
          <div className="flex flex-col gap-1.5">
            <input ref={(el) => (fileInputRef.current = el)} type="file" accept="image/*" hidden onChange={(e) => handleLogoUpload(e.target.files?.[0])} data-testid="branding-logo-input" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="branding-upload-btn">
              <ImageIcon weight="bold" className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload logo"}
            </Button>
            {form.brand_logo_url && (
              <Button onClick={handleLogoClear} variant="ghost" size="sm" className="h-7 text-[11px] text-red-600 hover:text-red-700 gap-1" data-testid="branding-remove-logo-btn">
                <XIcon weight="bold" className="w-3 h-3" /> Remove logo
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Colours */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Colours</h4>
        <p className="text-[11px] text-slate-500 -mt-1">Pick a hex colour, or leave blank to inherit. The accent is used for the thin top stripe and small dots.</p>
        <div className="grid grid-cols-3 gap-3">
          <ColorField label="Primary (header)" value={form.brand_primary} onChange={(v) => set("brand_primary", v)} fallback="#100f5d" testId="brand-primary" />
          <ColorField label="Accent (stripe)" value={form.brand_accent} onChange={(v) => set("brand_accent", v)} fallback="#c87d3a" testId="brand-accent" />
          <ColorField label="Text on primary" value={form.brand_text_on_primary} onChange={(v) => set("brand_text_on_primary", v)} fallback="#ffffff" testId="brand-text" />
        </div>
      </div>

      {/* Modal title */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Embedded modal title</Label>
        <Input
          value={form.brand_modal_title}
          onChange={(e) => set("brand_modal_title", e.target.value)}
          placeholder={`${referrer.company || referrer.name} · Instant Quote`}
          maxLength={80}
          data-testid="brand-modal-title-input"
        />
        <p className="text-[10px] text-slate-400">Shown across the top of the embedded modal on the referrer's website. Leave blank for "{referrer.company || referrer.name} · Instant Quote".</p>
      </div>

      {/* PDF / share-page footer override */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">PDF / share footer text</Label>
        <Input
          value={form.brand_footer_text}
          onChange={(e) => set("brand_footer_text", e.target.value)}
          placeholder="Auto: company · address · phone · email · VAT"
          maxLength={240}
          data-testid="brand-footer-text-input"
        />
        <p className="text-[10px] text-slate-400">
          Replaces the small line at the bottom of the quote PDF and share page. Blank = auto-compose from contact details. Use for company-number / regulated-by lines.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Terms &amp; Conditions URL</Label>
        <Input
          value={form.brand_terms_url}
          onChange={(e) => set("brand_terms_url", e.target.value)}
          placeholder="https://acme.example.com/terms"
          type="url"
          data-testid="brand-terms-url-input"
        />
        <p className="text-[10px] text-slate-400">When set, a "Terms &amp; Conditions" link is appended to the PDF + share-page footer.</p>
      </div>

      {/* Live preview band */}
      <div className="rounded-lg overflow-hidden border border-slate-200" data-testid="branding-preview">
        <div className="h-[3px]" style={{ background: previewAccent }} />
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: previewPrimary }}>
          <img src={logoSrc} alt="logo" className="h-8 max-w-[140px] object-contain" onError={(e) => { e.currentTarget.src = "/logo.png"; }} />
          <span className="text-xs font-semibold" style={{ color: previewText }}>Your Conveyancing Quote</span>
        </div>
        <div className="bg-white px-4 py-3 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full" style={{ background: `${previewPrimary}14`, color: previewPrimary }}>
            <span className="w-1 h-1 rounded-full" style={{ background: previewAccent }} /> Bespoke Quote Tool
          </span>
          <span className="text-xs font-bold" style={{ color: previewPrimary }}>Estimated Total · £1,250</span>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="save-branding-btn"
        className="w-full text-white hover:opacity-90" style={{ background: "#100f5d" }}>
        {saving ? "Saving…" : "Save branding"}
      </Button>
    </div>
  );
}

function ColorField({ label, value, onChange, fallback, testId }) {
  // Tolerate blank, hex shorthand and full hex. Color input itself only accepts
  // 7-char hex so we fall back when the saved value is empty.
  const display = /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value || "") ? value : fallback;
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-medium text-slate-600">{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={display}
          onChange={(e) => onChange(e.target.value)}
          className="w-9 h-9 rounded border border-slate-200 cursor-pointer shrink-0 bg-white"
          data-testid={`${testId}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Inherits ${fallback}`}
          className="h-9 text-xs font-mono"
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}



